/* eslint-disable jsx-a11y/alt-text */
import styled from '@emotion/styled';

import BaseLink from '@/components/common/BaseLink/BaseLink';
import { screen } from '@/components/common/breakpoints';
import { Image } from '@/components/common/image';
import { Heading, HeadingLevel, Paragraph } from '@/components/common/MarkUp';
import { bodySmall, captionLarge } from '@/styles/typography';

import type {
  BackgroundColourTokenType,
  SurfaceColourTokenType,
} from '@/types/colours';
import type { SanityImageAsset, SanityReference } from 'sanity-codegen';

export interface ProgrammeCardV2Props {
  image: {
    _type: 'image';
    asset: SanityReference<SanityImageAsset>;
  };
  duration?: string;
  title: string;
  body: string;
  tags: string[];
  trackingTag?: {
    _type: 'trackingTag';
    blockName: string;
    blockAction: string;
  };
  link: string;
  backgroundColourToken?: BackgroundColourTokenType | SurfaceColourTokenType;
}

const ProgrammeCardV2 = ({
  duration,
  title,
  body,
  tags,
  image,
  trackingTag,
  link = '',
  backgroundColourToken = '--surface-subtle',
}: ProgrammeCardV2Props) => {
  const tagBlocks = tags.map((tag) => {
    return tag && <Pill key={tag}>{tag}</Pill>;
  });
  return (
    <Row
      className={
        trackingTag?.blockName &&
        `tracking-${trackingTag.blockName}__${trackingTag.blockAction}`
      }
      backgroundColourToken={backgroundColourToken}
    >
      <HeadingLevel>
        <CardLink href={link} aria-label={`Link to ${title} programme`}>
          <ImageContainerRow>
            <Image {...image} isDecorativeImage />
          </ImageContainerRow>
          <CopyContainer>
            {duration && <DurationCaption>{duration}</DurationCaption>}
            <PostHeading size="body-md-semi-bold">{title}</PostHeading>
            <StyledParagraph size="body-sm">{body}</StyledParagraph>
            <PillContainer>{tagBlocks}</PillContainer>
          </CopyContainer>
        </CardLink>
      </HeadingLevel>
    </Row>
  );
};

const Row = styled.article<{ backgroundColourToken }>`
  background-color: ${({ backgroundColourToken }) =>
    `var(${backgroundColourToken})`};
  border-radius: var(--radius-l);
  transition: all 0.3s;
  overflow: hidden;
  width: 100%;
  display: flex;
  flex-direction: column;
  height: 100%;

  &:hover {
    transform: scale(1.03);
    cursor: pointer;
  }

  a {
    text-decoration: none;
  }
`;

const CardLink = styled(BaseLink)`
  user-select: none;
  -webkit-user-drag: none;
  height: 100%;
  display: flex;
  flex-direction: column;
  &:hover {
    text-decoration: none;
  }
  width: 100%;
`;

const ImageContainerRow = styled.div`
  display: flex;
  height: 168px;
  width: calc(100% + 1px);
  flex-direction: column;
  border-radius: calc(var(--border-radius-tiny) / 2)
    calc(var(--border-radius-tiny) / 2) 0 0;
  img {
    object-fit: cover;
    border-radius: calc(var(--border-radius-tiny) / 2)
      calc(var(--border-radius-tiny) / 2) 0 0;
    height: 100%;
  }
`;

const CopyContainer = styled.div`
  padding: var(--spacing-x-small) var(--spacing-small) var(--spacing-small);
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border-radius: 8px;

  p:first-of-type {
    display: -webkit-box;
    overflow: hidden;
    -webkit-box-orient: vertical;
    text-overflow: ellipsis;
  }
  p {
    -webkit-line-clamp: 3;
  }

  ${screen.md} {
    p {
      -webkit-line-clamp: 4;
    }
  }
`;

const StyledParagraph = styled(Paragraph)`
  flex-grow: 1;
  margin-bottom: var(--spacing-150);
  color: var(--text-strong);
`;

const PostHeading = styled(Heading)`
  font-weight: var(--font-weight-bold);
  padding-bottom: var(--spacing-150);
  color: var(--text-strong);

  &:hover {
    text-decoration: underline;
    text-decoration-color: var(--text-action);
    text-decoration-thickness: 1px;
  }
`;

const DurationCaption = styled.p`
  ${captionLarge}
  display: inline-block;
  padding-bottom: var(--spacing-150);
  color: var(--text-action);
`;

const PillContainer = styled.div`
  display: flex;
  max-width: 100%;
  flex-flow: wrap;
  gap: var(--spacing-xx-small);
  margin-top: var(--spacing-x-small);
`;

const Pill = styled.span`
  ${bodySmall}
  border-radius: 8px;
  padding: calc(var(--spacing-xxx-small) + 1px) var(--spacing-xx-small);
  background-color: var(--decorative-eclipse);
  color: var(--text-strong);
  font-weight: bold;
  height: min-content;
`;

export { ProgrammeCardV2 };
